import type { AdminOrderDetailDto, AdminOrderTimelineEntryDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';
import { formatDateTime } from '../../../utils/format';

/**
 * Таймлайн tab of the order detail drawer (AUT-123). Renders
 * `dto.timeline[]` (already newest-first from the backend) as a simple
 * vertical list of status transitions with timestamp + actor + optional
 * note. Empty-state when the timeline has no entries.
 */
const TimelineRow = ({ entry }: { entry: AdminOrderTimelineEntryDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {t(`orders.status.${entry.status}`, { defaultValue: entry.status })}
        </Typography>
        <Typography variant="caption" sx={{ color: tokens.text.tertiary, fontFamily: 'monospace' }}>
          {formatDateTime(entry.at)}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {t('orders.detail.timeline.actor', { actor: entry.actorType })}
      </Typography>
      {entry.note ? (
        <Typography variant="body2" color="text.secondary">
          {entry.note}
        </Typography>
      ) : null}
    </Box>
  );
};

export const TimelineTab = ({ order }: { order: AdminOrderDetailDto }): ReactElement => {
  const { t } = useTranslation();

  if (order.timeline.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('orders.detail.timeline.empty')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {order.timeline.map((entry, i) => (
        <TimelineRow key={`${entry.status}-${entry.at}-${i}`} entry={entry} />
      ))}
    </Box>
  );
};
